// Shared building blocks for tour definitions (framework overview in tour_controller.js).

import { extensionPresent } from '../controllers/helpers/extension_presence';

// Alert removal still counts: the first load after an install stamps too late. No alert at all (demo) counts too.
export function extensionDetected() {
  return extensionPresent() || !document.getElementById('extension-install-alert');
}

export function whenExtensionDetected(callback) {
  if (extensionDetected()) {
    callback();
    return () => {};
  }

  const observer = new MutationObserver(() => {
    if (!extensionDetected()) return;

    observer.disconnect();
    callback();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return () => observer.disconnect();
}

// Every match, not just the first: lite ships all rows in one stack and the tab filter d-nones the ones off
// the active tab, so on a ?tab= URL querySelector alone lands on a hidden row and waits there forever.
export function firstVisible(selector, within = document) {
  return [...within.querySelectorAll(selector)].find((element) => element.offsetParent !== null) ?? null;
}

// Polls: the extension, not this app, decides when the target appears.
export function whenElementVisible(selector, callback, { timeoutMs = 15000, within = document } = {}) {
  const intervalMs = 200;
  let elapsed = 0;

  const tick = () => {
    const element = firstVisible(selector, within);
    if (element) {
      clearInterval(timer);
      callback(element);
    } else if (elapsed >= timeoutMs) {
      clearInterval(timer);
    }
    elapsed += intervalMs;
  };

  const timer = setInterval(tick, intervalMs);
  tick();
  return () => clearInterval(timer);
}

// driver.js un-marks only the element it transitioned away from, so marks pile up over a long tour — and
// `.driver-active-element` lifts pointer-events through the overlay, leaving everything already toured
// clickable. Its aria goes with it; driver.js sets the pair together. Call from onHighlightStarted.
const ACTIVE_CLASS = 'driver-active-element';
const ACTIVE_ARIA = ['aria-haspopup', 'aria-expanded', 'aria-controls'];

export function dropStaleHighlights(current) {
  document.querySelectorAll(`.${ACTIVE_CLASS}`).forEach((element) => {
    if (element === current) return;

    element.classList.remove(ACTIVE_CLASS);
    ACTIVE_ARIA.forEach((attribute) => element.removeAttribute(attribute));
  });
}

// The sidebar's three lower menus arrive collapsed, so a step about a link inside one has to open it — and
// put the panel back on the way out. Shared by the tours that walk the menu.
const ACCORDION_SELECTOR = '#sidebarOptionsAccordion';
const PANEL_SELECTOR = '.accordion-collapse';
const TOGGLE_SELECTOR = '.accordion-button';
const OPEN_CLASS = 'show';

const panels = () => [...document.querySelectorAll(`${ACCORDION_SELECTOR} ${PANEL_SELECTOR}`)];
const isOpen = (panel) => panel.classList.contains(OPEN_CLASS);
const toggleFor = (panel) => document.querySelector(`${ACCORDION_SELECTOR} ${TOGGLE_SELECTOR}[data-bs-target="#${panel.id}"]`);

// driver.js measures the stage as soon as onHighlightStarted returns, so the panel must be at full height by
// then — Bootstrap's 350ms collapse would be measured mid-flight. Same end state, written in one go; Bootstrap
// reads `show` back off the DOM, so its own toggles still work afterwards.
const setPanelOpen = (panel, open) => {
  panel.classList.remove('collapsing');
  panel.classList.add('collapse');
  panel.classList.toggle(OPEN_CLASS, open);
  panel.style.height = '';

  const toggle = toggleFor(panel);
  toggle?.classList.toggle('collapsed', !open);
  toggle?.setAttribute('aria-expanded', String(open));
};

// A link in a shut panel has no layout box, but the tour opens that panel on the way past, so it counts as
// present. Gated on the accordion being displayed: below sm the sidebar is hidden with every panel shut,
// which would otherwise read as "collapsed, will open".
export function collapsedTarget(selector) {
  if (!firstVisible(ACCORDION_SELECTOR)) return null;

  return [...document.querySelectorAll(selector)].find((element) => element.closest(`${PANEL_SELECTOR}:not(.${OPEN_CLASS})`)) ?? null;
}

// data-bs-parent's one-panel-at-a-time rule, applied by hand for the same reason the expansion is.
export function revealPanelFor(element) {
  const panel = element?.closest(PANEL_SELECTOR) ?? null;
  if (!panel || isOpen(panel)) return;

  panels().forEach((other) => setPanelOpen(other, other === panel));
}

export function openPanelIds() {
  return new Set(
    panels()
      .filter(isOpen)
      .map((panel) => panel.id),
  );
}

export function restorePanels(ids) {
  panels().forEach((panel) => setPanelOpen(panel, ids.has(panel.id)));
}

// driver.js stamps this on <body> for the length of a run; tours.scss keys off it too.
const ACTIVE_BODY_CLASS = 'driver-active';

export function tourRunning() {
  return document.body.classList.contains(ACTIVE_BODY_CLASS);
}

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

// driver.js renders descriptions as HTML, so anything read off the page or out of the payload — tool names,
// domains, DB copy — is escaped before it lands in one. "Black & White" alone needs this.
export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => HTML_ENTITIES[character]);
}

// Escapes first, then promotes {braced} spans to <strong> — so copy can carry an accent without any of it
// being able to inject markup. Braces survive escaping, which is what makes the order safe.
export function accentedHtml(copy) {
  return escapeHtml(copy).replace(/\{([^{}]*)\}/g, (_match, accented) => `<strong>${accented}</strong>`);
}

// [text](href), promoted after accentedHtml has escaped everything — so a payload string becomes an href
// only if it is relative or http(s), never javascript:. New tab: a step's link would otherwise navigate the
// tour out from under itself.
const LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const SAFE_HREF = /^(https?:\/\/|\/)/;

export function linkedHtml(copy) {
  return accentedHtml(copy).replace(LINK_PATTERN, (match, text, href) => (SAFE_HREF.test(href) ? `<a href="${href}" target="_blank" rel="noopener">${text}</a>` : match));
}

// Server-rendered popover content, parked in an inert <template> so its images cost nothing until a step
// actually uses it. Returns the markup driver.js wants, or null when the page didn't render one.
export function templateHtml(id) {
  return document.getElementById(id)?.innerHTML ?? null;
}
